import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

/** Execution mode for review gates: draft always gates, assist gates on low confidence, auto publishes. */
export const executionModes = ['draft', 'assist', 'auto'] as const;
export const executionModeSchema = z.enum(executionModes);
export type ExecutionMode = z.infer<typeof executionModeSchema>;

/** Lifecycle of a workspace. */
export const workspaceStatuses = ['draft', 'live', 'archived'] as const;
export const workspaceStatusSchema = z.enum(workspaceStatuses);
export type WorkspaceStatus = z.infer<typeof workspaceStatusSchema>;

export const workspaceSettingsSchema = z
  .object({
    /** Optional external domain/label, e.g. "demo-insurance". */
    domain: z.string().max(120).optional(),
    description: z.string().max(2000).optional(),
  })
  .passthrough();
export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>;

export const workspaceSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    name: z.string().min(1).max(200),
    slug: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[a-z0-9-]+$/, 'slug must be kebab-case'),
    status: workspaceStatusSchema.default('draft'),
    defaultExecutionMode: executionModeSchema.default('assist'),
    settings: workspaceSettingsSchema.default({}),
  })
  .merge(timestampsSchema);
export type Workspace = z.infer<typeof workspaceSchema>;

export const createWorkspaceSchema = workspaceSchema
  .pick({
    name: true,
    slug: true,
    defaultExecutionMode: true,
  })
  .extend({
    status: workspaceStatusSchema.optional(),
    settings: workspaceSettingsSchema.optional(),
  });
export type CreateWorkspace = z.infer<typeof createWorkspaceSchema>;

export const updateWorkspaceSchema = createWorkspaceSchema.partial();
export type UpdateWorkspace = z.infer<typeof updateWorkspaceSchema>;

/** Valid lifecycle transitions: draft -> live -> archived (archived is terminal; live can revert to draft). */
export const workspaceTransitions: Record<WorkspaceStatus, WorkspaceStatus[]> = {
  draft: ['live', 'archived'],
  live: ['draft', 'archived'],
  archived: [],
};

export function canTransitionWorkspace(from: WorkspaceStatus, to: WorkspaceStatus): boolean {
  return from === to || workspaceTransitions[from].includes(to);
}
